import React, { createContext, useContext, useEffect, useState } from 'react'
import { withMeticulous } from '@/utils/meticulous'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
  handlePrint: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const useOptionalTheme = () => {
  return useContext(ThemeContext)
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light')
  const [mounted, setMounted] = useState(false)

  // Handle hydration mismatch by only setting theme after mount
  useEffect(() => {
    setMounted(true)
    const savedTheme = localStorage.getItem('theme') as Theme
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
      .matches
      ? 'dark' // TODO: change this to dark to support dark mode
      : 'light'
    const initialTheme = savedTheme || systemTheme
    setThemeState(initialTheme)
    applyTheme(initialTheme)
    void withMeticulous((meticulous) =>
      meticulous.context.recordCustomContext('theme', initialTheme)
    )
  }, [])

  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement
    if (newTheme === 'dark') {
      root.classList.add('dark') // TODO: change this to dark to support dark mode
      root.setAttribute('data-mode', 'dark') // TODO: change this to dark to support dark mode
    } else {
      root.classList.remove('dark')
      root.setAttribute('data-mode', 'light')
    }
  }

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('theme', newTheme)
    applyTheme(newTheme)
    void withMeticulous((meticulous) =>
      meticulous.context.recordCustomContext('theme', newTheme)
    )
  }

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }

  const handlePrint = () => {
    if (theme === 'dark') {
      toggleTheme()
      setTimeout(() => {
        window.print()
      }, 1000)
    } else {
      window.print()
    }
  }

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return <>{children}</>
  }

  const value = {
    theme,
    toggleTheme,
    setTheme,
    handlePrint,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
